import React, { useEffect, useRef, useState } from "react"
import { GlobeAltIcon, CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import { setLanguageGlobal } from "aldoo-ra/Localization"
import { GetSupportedLanguages } from "aldoo-ra/Localization/api"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"

let cached = null
let loading = null

// Supported site languages (POST /v1/localization {languages}); both the
// bare-array and {languages:[…]} response shapes are accepted.
function useSupportedLanguages() {
  const [list, setList] = useState(cached || [])
  useEffect(() => {
    if (cached) return setList(cached)
    loading ||= GetSupportedLanguages()
      .then((res) => {
        cached = Array.isArray(res) ? res : res?.languages || []
        return cached
      })
      .catch(() => [])
    loading.then(setList)
  }, [])
  return list
}

function useOutsideClose(ref, open, close) {
  useEffect(() => {
    if (!open) return
    const onDown = (e) => {
      if (!ref.current?.contains(e.target)) close()
    }
    const onKey = (e) => e.key === "Escape" && close()
    document.addEventListener("mousedown", onDown)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("mousedown", onDown)
      document.removeEventListener("keydown", onKey)
    }
  }, [open, ref, close])
}

/**
 * Site language switcher backed by aldoo-ra Localization
 * (setLanguageGlobal rewrites the /xx URL prefix and the localization store).
 * variant "menu": compact "EN" button (top bar); "select": bordered box with
 * the full language name (footer, mobile menu).
 */
export default function LanguageSwitcher({ variant = "menu", className = "" }) {
  const language = useSiteLanguage()
  const languages = useSupportedLanguages()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const close = () => setOpen(false)
  useOutsideClose(ref, open, close)

  const selected = languages.find((l) => l.code === language)
  const choose = (code) => {
    close()
    if (code !== language) setLanguageGlobal(code)
  }

  const isSelect = variant === "select"

  return (
    <div ref={ref} className={twMerge("relative", className)} onMouseLeave={isSelect ? undefined : close}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={isSelect ? undefined : () => setOpen(true)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={twMerge(
          "inline-flex items-center gap-1 text-white text-[15px] font-medium",
          isSelect ? "min-w-[160px] gap-2 rounded border border-white/40 px-3 py-2 hover:border-white/70 hover:bg-white/5" : "px-2 h-[40px]"
        )}
      >
        <GlobeAltIcon className={isSelect ? "w-[18px] h-[18px] shrink-0" : "w-4 h-4"} />
        {isSelect ? <span className="flex-1 text-left">{selected?.displayName || language.toUpperCase()}</span> : (selected?.code || language).toUpperCase()}
        {isSelect && <ChevronDownIcon className="w-4 h-4 shrink-0" />}
      </button>

      {open && (
        <ul
          role="listbox"
          className={twMerge(
            "absolute left-0 top-full z-[500] mt-1 min-w-[160px] rounded-md border border-white/20 bg-dark py-1 shadow-[0_8px_24px_rgba(0,0,0,0.6)] list-none m-0 p-0",
            isSelect && "min-w-full"
          )}
        >
          {languages.map(({ code, displayName }) => {
            const active = code === language
            return (
              <li key={code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => choose(code)}
                  className={twMerge(
                    "flex w-full items-center justify-between gap-4 px-4 py-[10px] text-[15px] hover:bg-white/10 hover:text-white",
                    active ? "text-white font-semibold" : "text-white/60"
                  )}
                >
                  {displayName}
                  {active && <CheckIcon className="w-4 h-4 text-white stroke-2" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
